import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { centerToast } from '../components/CenterToast';
import { ArrowLeft, Clock, Users, DollarSign } from 'lucide-react';

const FOLLOW_UP_SERVICES = [
  { id: 'full-pay',  label: 'Pay fully for Burial service package selected (Comprehensive Package - $2,200)' },
  { id: 'tahlil',    label: 'Tahlil at home service (Estimated: $100 - $200)' },
  { id: 'badal',     label: 'Badal Haji service (Estimated: $1,000 - $1,200)' },
  { id: 'legal',     label: 'Faraid, Wasiat & Legal services' },
  { id: 'hibah',     label: 'Gift/Hibah to Darul Ihsan orphanages' }
];

export default function MyPastBurialDetail({ entry, onBack }) {
  const [selected, setSelected] = useState({});

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const handleSubmit = () => {
    const chosen = FOLLOW_UP_SERVICES.filter((s) => selected[s.id]);
    if (chosen.length === 0) {
      toast.error('Please select at least one service', { position: 'top-center' });
      return;
    }
    centerToast(`Request submitted. A representative will contact you shortly regarding ${chosen.length} service${chosen.length > 1 ? 's' : ''}.`, '📨');
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="bg-card border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={onBack} className="gap-2" data-testid="burial-detail-back">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <h1 className="text-lg sm:text-xl font-semibold">Burial Summary</h1>
          <div className="w-16" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {entry && (
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-semibold">{entry.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{entry.date} · {entry.location}</p>
          </div>
        )}

        {/* Summary Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total duration</p>
                <p className="font-semibold text-base" data-testid="metric-duration">4 Hours 30 Mins</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total RSVPs</p>
                <p className="font-semibold text-base" data-testid="metric-rsvps">145 people</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total donated</p>
                <p className="font-semibold text-base" data-testid="metric-donated">$3,000.00</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Follow-up Services */}
        <Card>
          <CardContent className="p-5 sm:p-6 space-y-5">
            <div>
              <h3 className="text-base sm:text-lg font-semibold">Follow-up Services</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Select the services you require. An official representative will contact you shortly after submission.
              </p>
            </div>

            <div className="space-y-3">
              {FOLLOW_UP_SERVICES.map((svc) => (
                <label
                  key={svc.id}
                  htmlFor={`svc-${svc.id}`}
                  className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted/40 transition-colors"
                >
                  <Checkbox
                    id={`svc-${svc.id}`}
                    data-testid={`svc-${svc.id}`}
                    checked={!!selected[svc.id]}
                    onCheckedChange={() => toggle(svc.id)}
                    className="mt-0.5"
                  />
                  <Label htmlFor={`svc-${svc.id}`} className="text-sm leading-relaxed cursor-pointer font-normal">
                    {svc.label}
                  </Label>
                </label>
              ))}
            </div>

            <Button
              data-testid="submit-request"
              onClick={handleSubmit}
              size="lg"
              className="w-full h-12 text-base"
            >
              Submit Request
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
